import { useEffect, useRef, useState } from "react"
import type { AppShell } from "@/App"
import { errorMessage, getGoogleMapsConfig, type Order, type Warehouse } from "@/lib/api"
import { n } from "@/lib/format"
import { loadGoogleMaps, onGoogleMapsProblem } from "@/lib/google-maps"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type G = any

/** Gota 24×34 (bico em 12,34). */
const PIN_PATH = "M12 0C5.37 0 0 5.37 0 12c0 8.4 12 22 12 22s12-13.6 12-22C24 5.37 18.63 0 12 0z"

let keyPromise: Promise<{ enabled: boolean; apiKey: string | null }> | null = null

export interface MapPin {
  order: Order
  color: string
  /** Contorno: a cor do veículo ou da transportadora atribuída (null = branco). */
  ring?: string | null
  /** Esbatido: não passa nos filtros, mas fica no mapa para dar contexto. */
  dim: boolean
  selected: boolean
}

/**
 * Mapa do Despacho: um pino por encomenda localizada, com a cor do destino e a
 * letra da classe de carga. Clique abre o detalhe; Shift + clique junta à
 * seleção. Enquadra as encomendas só na primeira vez (não salta a cada atribuição).
 */
export function DispatchMap({
  shell,
  pins,
  warehouse,
  onPick,
  onToggle,
}: {
  shell: AppShell
  pins: MapPin[]
  warehouse: Warehouse | null
  onPick: (orderId: string) => void
  onToggle: (orderId: string) => void
}) {
  const box = useRef<HTMLDivElement>(null)
  const map = useRef<G>(null)
  const markers = useRef<Map<string, G>>(new Map())
  const home = useRef<G>(null)
  const framed = useRef(false)
  const handlers = useRef({ onPick, onToggle })
  handlers.current = { onPick, onToggle }
  const [state, setState] = useState<"loading" | "ready" | "off" | string>("loading")
  const [problem, setProblem] = useState<string | null>(null)
  useEffect(() => onGoogleMapsProblem(setProblem), [])

  useEffect(() => {
    let cancelled = false
    keyPromise ??= getGoogleMapsConfig(shell)
    keyPromise
      .then(async (cfg) => {
        if (!cfg.enabled || !cfg.apiKey) return setState("off")
        const google = await loadGoogleMaps(cfg.apiKey)
        const { Map } = await google.maps.importLibrary("maps")
        if (cancelled || !box.current) return
        // O mapa é o ecrã inteiro: a roda faz zoom sem Ctrl (dentro do iframe a
        // Google põe-no em modo cooperativo) e os botões +/− ficam visíveis.
        map.current ??= new Map(box.current, { center: { lat: 39.6, lng: -8.6 }, zoom: 8, gestureHandling: "greedy", zoomControl: true, mapTypeControl: false, streetViewControl: false, fullscreenControl: true, clickableIcons: false })
        setState("ready")
      })
      .catch((err) => {
        keyPromise = null
        setState(errorMessage(err))
      })
    return () => {
      cancelled = true
    }
  }, [shell])

  useEffect(() => {
    if (state !== "ready" || !map.current) return
    const google = (window as unknown as { google: G }).google
    const seen = new Set<string>()
    const bounds = new google.maps.LatLngBounds()
    // Enquadra pelo continente quando há encomendas lá: os Açores e a Madeira
    // (a oeste de -12°) deixavam o país minúsculo. Os pinos das ilhas ficam.
    const mainland = new google.maps.LatLngBounds()
    for (const p of pins) {
      const lat = n(p.order.lat)
      const lng = n(p.order.lng)
      if (lat === null || lng === null) continue
      seen.add(p.order.id)
      bounds.extend({ lat, lng })
      if (lng > -12) mainland.extend({ lat, lng })
      // Pino em gota (bico no ponto exato), com a classe de carga lá dentro.
      const size = p.selected ? 1.45 : 1.2
      const icon = {
        path: PIN_PATH,
        anchor: new google.maps.Point(12, 34),
        labelOrigin: new google.maps.Point(12, 12),
        scale: size,
        fillColor: p.color,
        fillOpacity: p.dim ? 0.45 : 1,
        strokeColor: p.selected ? "#111827" : p.ring ?? "#ffffff",
        strokeWeight: p.selected ? 3 : p.ring ? 3 : 1.5,
      }
      const label = { text: p.order.load_class ?? "·", color: p.dim ? "rgba(255,255,255,.75)" : "#fff", fontSize: p.selected ? "13px" : "11px", fontWeight: "700" }
      const title = `${p.order.source_ref} · ${p.order.locality ?? ""} · ${p.order.customer_name ?? ""}`
      let m = markers.current.get(p.order.id)
      if (!m) {
        m = new google.maps.Marker({ map: map.current, position: { lat, lng }, icon, label, title })
        const id = p.order.id
        m.addListener("click", (e: { domEvent?: MouseEvent }) => (e.domEvent?.shiftKey ? handlers.current.onToggle(id) : handlers.current.onPick(id)))
        markers.current.set(id, m)
      } else {
        m.setIcon(icon)
        m.setLabel(label)
        m.setTitle(title)
        m.setPosition({ lat, lng })
      }
      m.setZIndex(p.selected ? 2000 : p.dim ? 1 : p.ring ? 200 : 100)
    }
    for (const [id, m] of markers.current) {
      if (!seen.has(id)) {
        m.setMap(null)
        markers.current.delete(id)
      }
    }
    const wlat = n(warehouse?.lat)
    const wlng = n(warehouse?.lng)
    if (wlat !== null && wlng !== null) {
      home.current ??= new google.maps.Marker({ map: map.current, position: { lat: wlat, lng: wlng }, title: warehouse?.name ?? "Armazém", label: { text: "A", color: "#fff", fontWeight: "700" }, zIndex: 3000 })
      bounds.extend({ lat: wlat, lng: wlng })
      if (wlng > -12) mainland.extend({ lat: wlat, lng: wlng })
    }
    if (!framed.current && !bounds.isEmpty()) {
      map.current.fitBounds(mainland.isEmpty() ? bounds : mainland, 40)
      framed.current = true
    }
  }, [state, pins, warehouse])

  // Ao abrir uma encomenda, centra-a (sem mudar o zoom).
  const selectedId = pins.find((p) => p.selected)?.order.id ?? null
  useEffect(() => {
    if (state !== "ready" || !selectedId) return
    const m = markers.current.get(selectedId)
    if (m) map.current?.panTo(m.getPosition())
  }, [selectedId, state])

  return (
    <div className="relative h-full min-h-[460px] overflow-hidden rounded-xl border bg-muted">
      <div ref={box} className="absolute inset-0" />
      {state !== "ready" && (
        <div className="absolute inset-0 flex items-center justify-center p-4 text-center text-[12.5px] text-muted-foreground">
          {state === "loading" ? "A carregar o mapa…" : state === "off" ? "Mapa indisponível: o Google Maps não está configurado no tenant (Definições → Integrações)." : `Mapa indisponível: ${state}`}
        </div>
      )}
      {problem && (
        <div className="absolute inset-x-3 top-12 z-10 rounded-lg border border-amber-300 bg-amber-50/95 px-3 py-2 text-[12.5px] text-amber-900 shadow-sm">
          <b>Google Maps com problema na chave.</b> {problem}
        </div>
      )}
    </div>
  )
}
